import { Trans, useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import { CountryReturnRate } from '@/domain/returns'
import { currencySymbol } from '@/utils/format'
import {
  RateCell,
  TOOLTIP_TAGS
} from '@/ui/components/analytics-components/rate-table'

const { Card, InfoTooltip, Table, createColumnHelper } = Base

type OrderValue = keyof CountryReturnRate

const columnHelper = createColumnHelper<CountryReturnRate>()

interface CountriesReturnRateTableProps {
  entries: CountryReturnRate[]
  totalEntries: number
  perPage: number
  pageNumber: number
  orderValue: string
  orderDirection: boolean
  setPageNumber: (pageNumber: number) => void
  setOrderValue: (value: OrderValue) => void
  setOrderDirection: (value: boolean) => void
  chartMetric:
    | 'RETURN_RATE_CHART_METRIC_UNSPECIFIED'
    | 'RETURN_RATE_CHART_METRIC_QUANTITY'
    | 'RETURN_RATE_CHART_METRIC_AMOUNT'
  shopCurrency: string
}

export const CountriesReturnRateTable: React.FC<
  CountriesReturnRateTableProps
> = ({
  entries,
  totalEntries,
  perPage,
  pageNumber,
  orderValue,
  orderDirection,
  setPageNumber,
  setOrderValue,
  setOrderDirection,
  chartMetric,
  shopCurrency
}) => {
  const { t, i18n } = useTranslation()
  const isAmount = chartMetric === 'RETURN_RATE_CHART_METRIC_AMOUNT'
  const symbol = currencySymbol(shopCurrency, i18n.language)
  const unit = isAmount ? ` (${symbol})` : ''

  const formatAmount = (value: number | undefined) =>
    isAmount
      ? (value ?? 0).toLocaleString(i18n.language, {
          style: 'currency',
          currency: shopCurrency
        })
      : (value ?? 0).toLocaleString()

  const columns = [
    columnHelper.accessor('country', {
      header: t('product_analytics.country'),
      enableSorting: false
    }),
    columnHelper.accessor('itemsPurchased', {
      header: `${t('return_rate_table.items_purchased')}${unit}`,
      cell: ({ getValue }) => formatAmount(getValue()),
      meta: {
        headInfo: (
          <InfoTooltip
            label={t('return_rate_table.about_items_purchased')}
            content={t('return_rate_table.purchased_description')}
          />
        )
      }
    }),
    columnHelper.accessor('itemsReturned', {
      header: `${t('return_rate_table.items_returned')}${unit}`,
      cell: ({ getValue }) => formatAmount(getValue()),
      meta: {
        headInfo: (
          <InfoTooltip
            label={t('return_rate_table.about_items_returned')}
            content={t('return_rate_table.returned_description')}
          />
        )
      }
    }),
    columnHelper.accessor('returnRate', {
      header: t('return_rate_table.return_rate'),
      cell: ({ getValue }) => <RateCell value={getValue()} />,
      meta: {
        headInfo: (
          <InfoTooltip
            label={t('return_rate_table.about_return_rate')}
            content={
              <Trans
                i18nKey="return_rate_table.return_rate_description"
                components={TOOLTIP_TAGS}
              />
            }
          />
        )
      }
    }),
    columnHelper.accessor('refundRate', {
      header: t('return_rate_table.refund_rate'),
      cell: ({ getValue }) => <RateCell value={getValue()} />,
      meta: {
        headInfo: (
          <InfoTooltip
            label={t('return_rate_table.about_refund_rate')}
            content={
              <Trans
                i18nKey="return_rate_table.refund_rate_description"
                components={TOOLTIP_TAGS}
              />
            }
          />
        )
      }
    }),
    columnHelper.accessor('rdv', {
      header: t('return_rate_table.rdv'),
      cell: ({ getValue }) => <RateCell value={getValue()} />,
      meta: {
        headInfo: (
          <InfoTooltip
            label={t('return_rate_table.about_rdv')}
            content={
              <Trans
                i18nKey="return_rate_table.rdv_description"
                components={TOOLTIP_TAGS}
              />
            }
          />
        )
      }
    })
  ]

  return (
    <Card className="h-auto overflow-hidden">
      <Table
        data={entries}
        columns={columns}
        // The caller sorts every country before slicing out this page.
        manualSorting
        sorting={[{ id: orderValue, desc: !orderDirection }]}
        onSortingChange={([sort]) => {
          if (!sort) return
          setOrderValue(sort.id as OrderValue)
          setOrderDirection(!sort.desc)
        }}
        pagination={{
          pageIndex: pageNumber - 1,
          pageSize: perPage,
          pageCount: Math.ceil(totalEntries / perPage),
          onPageChange: (index) => setPageNumber(index + 1),
          previousLabel: t('return_rate_table.previous_page'),
          nextLabel: t('return_rate_table.next_page')
        }}
      />
    </Card>
  )
}
